import {
  NativeId,
  getNativeNodeByNativeIdPath,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  CMS_COLLECTION_ID_ATTR,
  CMS_ITEM_ID_ATTR,
  CMS_COLLECTION_SOURCE_ATTR,
} from '@packages/systems/core/constants/SharedConfig';

/**
 * Returns the collection data for a given native node or native id path.
 *
 * Although at first glance this helper should be contained in a browser-only package,
 * the fact is that there are certain places where it is imported and can fail
 * because browser-only code ends up being included in the Next.js SSR environment:
 *
 * packages/domains/authorization/client/sdks/designer/utils/isElementInteractionRestricted.ts
 *
 * @param nativeIdPath - The native id path of the node
 * @param nativeNode - The native node
 * @returns The collection data for the node
 */
export function getNativeNodeCollectionData({
  nativeIdPath,
  nativeNode,
}: {
  nativeIdPath?: NativeId[] | null;
  nativeNode?: HTMLElement | null;
}): {
  isEmptyNode: boolean;
  isElementBoundToCMS: boolean;
  collectionId: string | null;
  collectionItemId: string | null;
} {
  if (!nativeIdPath && !nativeNode) {
    return {
      isEmptyNode: false,
      collectionId: null,
      collectionItemId: null,
      isElementBoundToCMS: false,
    };
  }

  // We accept either a nativeIdPath or the node itself. If just the path is passed in,
  // we need to get the node. If the node is passed in, we use that.
  let _nativeNode = nativeNode;
  if (nativeIdPath && !nativeNode) {
    _nativeNode = getNativeNodeByNativeIdPath(nativeIdPath);
  }

  if (!_nativeNode) {
    return {
      isEmptyNode: false,
      collectionId: null,
      collectionItemId: null,
      isElementBoundToCMS: false,
    };
  }

  const collectionId = _nativeNode.getAttribute(CMS_COLLECTION_ID_ATTR);
  const collectionItemId = _nativeNode.getAttribute(CMS_ITEM_ID_ATTR);
  const collectionSource = _nativeNode.getAttribute(CMS_COLLECTION_SOURCE_ATTR);

  const isEmptyNode = _nativeNode.classList.contains('wf-empty');

  return {
    isEmptyNode,
    collectionId,
    collectionItemId,
    isElementBoundToCMS: collectionSource === 'cms',
  };
}
